import { repairGermanProductUI } from '@theme/zentus-german-product-locale';
import { Component } from '@theme/component';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /** @type {symbol | null} Owns both network and cached response callbacks. */
  #loadRequest = null;

  connectedCallback() {
    super.connectedCallback();
    repairGermanProductUI(this);
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#activeFetch?.abort();
    this.#loadRequest = null;
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;
    const request = Symbol();
    this.#loadRequest = request;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent, id)
      .then((result) => {
        if (this.#loadRequest !== request) return;
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const recommendations = this.#readRecommendations(result.data, id);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          delete this.dataset.error;
          repairGermanProductUI(recommendations);
          this.innerHTML = recommendations.innerHTML;
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        if (this.#loadRequest !== request || e.name === 'AbortError') return;
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @param {string} id
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent, id) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    this.#activeFetch?.abort();

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    const controller = new AbortController();
    this.#activeFetch = controller;

    try {
      // Shopify can intermittently fail while loading translated recommendations.
      // Keep the server-rendered cards visible and retry only transient server errors.
      for (let attempt = 0; ; attempt++) {
        if (controller.signal.aborted) throw new DOMException('Recommendation request cancelled', 'AbortError');
        const response = await fetch(url, { signal: controller.signal });
        if (controller.signal.aborted) throw new DOMException('Recommendation request cancelled', 'AbortError');
        if (response.ok) {
          const text = await response.text();
          if (controller.signal.aborted) throw new DOMException('Recommendation request cancelled', 'AbortError');
          // HTTP 200 can still contain Shopify's error document or an incomplete section.
          if (this.#readRecommendations(text, id)) {
            this.#cachedRecommendations[url] = text;
            return { success: true, data: text };
          }
        }

        if (attempt < 2 && (response.ok || [500, 502, 503, 504].includes(response.status))) {
          await new Promise((resolve, reject) => {
            const cancel = () => {
              clearTimeout(timer);
              reject(new DOMException('Recommendation request cancelled', 'AbortError'));
            };
            const timer = setTimeout(() => {
              controller.signal.removeEventListener('abort', cancel);
              resolve();
            }, attempt === 0 ? 1000 : 3000);
            controller.signal.addEventListener('abort', cancel, { once: true });
          });
          continue;
        }
        if (response.ok) throw new Error('Invalid recommendation response');
        return { success: false, status: response.status };
      }
    } finally {
      if (this.#activeFetch === controller) this.#activeFetch = null;
    }
  }

  /**
   * Validate before caching or replacing the existing cards. Empty generic recommendation
   * sections are legitimate; Zentus fallback cards must never be replaced with an empty result.
   * @param {string} text
   * @param {string} id
   * @returns {HTMLElement | null}
   */
  #readRecommendations(text, id) {
    const html = new DOMParser().parseFromString(text, 'text/html');
    if (html.body.classList.contains('status-error') || html.querySelector('[data-i18n="try-refreshing"]')) return null;
    const recommendations = html.getElementById(id);
    if (recommendations?.tagName !== 'PRODUCT-RECOMMENDATIONS') return null;
    if (/Liquid (?:error|syntax error)\s*(?:\(|:)/i.test(recommendations.textContent)) return null;
    if (this.querySelector('.z-related-grid .z-product-card') &&
        !recommendations.querySelector('.z-related-grid .z-product-card')) return null;
    return recommendations;
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.dataset.error = 'Error loading product recommendations';
    // Keep server-rendered Zentus collection cards when recommendations fail.
    if (this.querySelector('.z-related-grid .z-product-card')) return;
    this.classList.add('hidden');
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
